import React, {
  MutableRefObject,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMemoizedFn, useRequest } from 'ahooks';
import { ProTablePlus, useScreenAdapterContext } from '@bedrock/eed-react';
import CenterLoading from '@mpc/common/src/components/CenterLoading';
import { IProTableRef } from '@bedrock/eed-react/lib/components/ProTable/interface';
import { IProFilterGroupRef } from '@bedrock/eed-react/lib/components/ProFilter/components/FilterGroup';
import moment from 'moment';
import classNames from 'classnames/bind';
import { $sType, localPx2rem } from '@mpc/common/src/utils';
import _ from 'lodash';
import qs from 'qs';
import styles from './index.less';
import {
  getColumnsConfig,
  defaultColumns,
  FILTER_OPTIONS,
  FILTER_ITEM_CONF,
  defaultFilterOptionsApi,
  getFilterOptions,
  findRecordFormMutilTreeByKey,
} from './utils';
import { getKey } from './utils';
import PageFooter from '#features/PageFooter';
import { defaultTableCellRender } from '#features/utils';
import { IToolBarProps } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';

const cx = classNames.bind(styles);

const OA_STATUS_KEY_MAP = {
  oa_not_submit: 'NOT_SUBMITTED',
  oa_submitted: 'SUBMITTED',
  oa_rejected: 'REJECTED',
  oa_archived: 'ARCHIVING',
};

const formatDataSource = (data: any = []) => {
  return data.map((item) => {
    // 有子单 且 没有children 字段时，需要手动添加children字段
    if (item.children_size > 0 && !item.children) {
      return { ...item, children: [] };
    }
    return item;
  });
};

export interface MPCListRef {
  tableRef: MutableRefObject<IProTableRef<any>>;
  filterRef: MutableRefObject<IProFilterGroupRef>;
  selectRows: any[];
  tableData: {
    list: any[];
    total: number;
    columnsData: { name: string; key: string }[];
  };
  refreshTable: () => void;
}

export interface MPCListProps {
  /** 菜单key */
  menuKey?: string;
  /** 分组类型 */
  group_type?: string;
  /** 是否展示表格勾选 */
  hasSelect?: boolean;
  /** 是否树形表格，目前仅用于控制是树形表格时的缩进对齐 */
  isTreeTable?: boolean;
  /** 展开icon所在列的key */
  expandIconColumnKey?: string;
  /** 表格rowKey */
  rowKey?: string;
  /** 筛选条件配置 */
  filterOptions?: {
    filterKeys?: Array<
      string | { optionKey: string; searchKey: string; name?: string, options?: any[],
        /** 不发起获取options请求 */
        noFetchOption?: boolean,
        /** 筛选类型 */
        filterType?: FilterType,
      }
    >;
  };
  /** 自定义列配置，用于整体覆盖基础配置(基础配置没有时，请先添加基础配置) */
  customColumnsConf?: Record<string, { render?: (v, record, index) => any; [key: string]: any }>;
  /** ProTablePlus组件的props */
  ProTablePlusProps?: React.ComponentProps<typeof ProTablePlus>;
  /** 获取筛选项下拉选项 */
  fetchFilterOptions?: () => Promise<{
    data?: {
      key?: string;
      name?: string;
      options?: { value?: any; name?: string }[];
    }[];
  }>;
  /** 获取列表数据 */
  fetchList: (params: {
    current: number;
    pageSize: number;
    conditions: any;
  }) => Promise<{
    data?: { list?: any[]; total_count?: number; columns?: any[] };
  }>;
  /** 获取子列表数据 */
  fetchChildList?: (record: any) => Promise<{ data?: any[] }>;
  /** 批量操作 渲染 */
  batchRender?: (params: {
    selectRows: any;
    tableData: any;
  }) => React.ReactNode[];
  /** 获取禁用勾选行key */
  getDisbaleCheckRowkeys?: (rows: any[]) => any[];
  /** 工具栏配置 */
  toolbarConfig?: IToolBarProps;
  HeaderPlace?: React.ReactNode;
  warpperClassName?: string;
  contentClassName?: string;
  /** 行点击 */
  onClickRow?: (record: any) => void;
}

const MPCList: React.ForwardRefRenderFunction<MPCListRef, MPCListProps> = (props, ref) => {
  const { menuKey, group_type = 'CHANNEL_GROUP', hasSelect = false, rowKey = 'id', filterOptions = FILTER_OPTIONS, 
    ProTablePlusProps, expandIconColumnKey = 'name', onClickRow,
    toolbarConfig,
  } = props;
  const { filterKeys } = filterOptions;
  const {
    fetchFilterOptions,
    fetchList,
    fetchChildList,
    batchRender,
    getDisbaleCheckRowkeys,
  } = props;

  const [tableData, setTableData] = useState({
    list: [],
    total: 0,
    // columnsData: defaultColumns,
    columnsData: [],
  });
  const [selectRows, setSelectRows] = useState([]);
  const [disabledKeys, setDisabledKeys] = React.useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [isShowLoading, setIsShowLoading] = useState(false);
  const [detail, setDetail] = useState<any>({});
  const location = useLocation();
  const history = useHistory();
  const urlParams = useParams<{ projectId?: string }>();
  const filterRef = React.useRef<IProFilterGroupRef>();
  const tableRef = React.useRef<IProTableRef<any>>();
  const { viewBase } = useScreenAdapterContext();

  const handleClickRow = useMemoizedFn((r: any) => {
    const queryParams = qs.stringify({
      projectId: r?.id,
      groupType: r?.group_type,
    });
    r?.id && history.push({ ...location, search: `?${queryParams}` });
  });

  const refreshTable = useMemoizedFn(() => {
    tableRef?.current?.query?.();
  });

  // 获取筛选条件
  const { data: filterOptionsData } = useRequest<any, any>(
    () => {
      if (fetchFilterOptions) {
        return fetchFilterOptions();
      }
      return defaultFilterOptionsApi(props).then((res) => {
        const { data: filterOptionsRes = [] } = res as any;
        const idx = filterOptionsRes.findIndex(
          (item) => item.key === 'oa_status'
        );
        if (idx > -1) {
          filterOptionsRes[idx].options = filterOptionsRes[idx].options?.map(
            (item) => {
              return {
                ...item,
                value: OA_STATUS_KEY_MAP[item.value] || item.value,
              };
            }
          );
        }
        return res;
      });
    },
    {
      refreshDeps: [fetchFilterOptions, filterKeys, group_type, menuKey],
    }
  );

  const memoColumns = useMemo(() => {
    const columnsConfig = {
      ...getColumnsConfig(),
      ...props.customColumnsConf,
    };
    const tableColumns =
      tableData?.columnsData?.map?.((item) => {
        const isOperationCol = item.key === 'operation';
        const width = columnsConfig?.[item.key]?.width || (isOperationCol ? 52 : 120);
        return {
          // 需求单、市场 后端返回的操作列的名 名称就叫【操作列】，所以这里要特殊处理为【操作】
          title: isOperationCol ? '操作' : item.name,
          // 列宽可调整
          resizable: true,
          dataIndex: item.key,
          fixed: isOperationCol ? 'right' : '',
          render: defaultTableCellRender,
          ...columnsConfig[item.key],
          width: typeof width === 'number' ? width * viewBase : width,
        };
      }) || [];
    const filterItems = (filterKeys || []).map((key) => {
      const searchKey = getKey(key, 'searchKey');
      const optionKey = getKey(key, 'optionKey');
      const filterOptions = filterOptionsData?.data?.find(
        (item) => item.key === optionKey
      );
      const filterItemConf = FILTER_ITEM_CONF[searchKey];
      {
        !filterItemConf &&
          console.warn(`未配置 ${searchKey} 的搜索项配置，请添加:` + 'features\\mpc-common\\src\\features\\MPCList\\utils.tsx,FILTER_ITEM_CONF');
      }
      const name = (key as any)?.name || filterOptions?.name || filterItemConf?.name;
      const options = getFilterOptions({
        fetchedFilterOptions: filterOptions?.options,
        localOptions: filterItemConf?.filterComponentProps?.options,
        paramOptions: (key as any)?.options,
      });
      return {
        hideInTable: true,
        title: name,
        dataIndex: searchKey,
        searchRender: {
          ...filterItemConf,
          type: (key as any)?.filterType || filterItemConf?.type,
          name,
          value: searchKey,
          filterComponentProps: {
            ...filterItemConf?.filterComponentProps,
            options,
          },
        },
      };
    });
    return [...tableColumns, ...filterItems];
  }, [
    filterOptionsData,
    tableData.columnsData,
    filterKeys,
    props.customColumnsConf,
  ]);  

  const queryService = async (query) => {
    setIsShowLoading(true);
    const params = query?.params;
    const { current, pageSize, search_text, paramsWithConditions } = params;
    const conditions = Object.entries(paramsWithConditions).reduce(
      (pre, [key, value]: [string, any]) => {
        if (!value?.value?.includes?.('__SELECT_ALL__')) {
          if (value.type === 'date_range') {
            pre[key] = value.value?.length
              ? value.value?.map((item) => moment(item).format('YYYY-MM-DD'))
              : undefined;
          } else {
            pre[key] = value.value;
          }
        }
        // 兼容 paramsWithConditions 里 search_text 为空时，取 params.search_text 的逻辑（目前的场景为先搜其他选项时，paramsWithConditions.searcj_text.value 为 undefined）
        if (key === 'search_text') {
          pre[key] = value.value || search_text;
        }
        return pre;
      },
      { search_text }
    );
    try {
      const res = await fetchList({
        current,
        pageSize,
        conditions,
      });
      setIsShowLoading(false);
      const dataSource = res.data?.list || [];
      const total = res.data?.total_count || 0;
      const columnsData = !res.data?.columns?.length
        ? [{ name: '', key: 'empty' }]
        : res.data?.columns;
      // 如果当前页没有数据，但是总条数大于0，则跳转到最后一页
      if (dataSource.length === 0 && total > 0 && current > 1) {
        let pageNum = total / pageSize;
        if (pageNum % 1 !== 0) {
          pageNum = Math.ceil(pageNum);
        }
        if (pageNum >= current) {
          console.warn(
            'are you ok',
            `总条数是 ${total}，每页 ${pageSize} 条，第 ${current} 页居然没有数据`
          );
        } else {
          tableRef.current?.query?.({ current: pageNum });
        }
      }
      const data = {
        list: formatDataSource(dataSource),
        total,
      };
      setTableData({ ...data, columnsData });
      setSelectRows([]);
      getDisbaleCheckRowkeys &&
        setDisabledKeys(getDisbaleCheckRowkeys(data.list));
      setExpandedRowKeys([]);
      return { data }
    } catch (error) {
      console.log('error', error);
      setIsShowLoading(false);
      setSelectRows([]);
      return { data: { list: [], total: 0 } };
    }
  };

  const handleExpand = (expanded, record) => {
    if (!expanded) return;
    fetchChildList?.(record).then((res: any) => {
      const tableDataItem = findRecordFormMutilTreeByKey(
        tableData.list,
        record.id
      );
      tableDataItem.children = formatDataSource(res.data);
      setTableData({ ...tableData, list: [...tableData.list] });
      tableRef.current?.updateData?.(tableData.list);
    });
  };

  useImperativeHandle(ref, () => ({
    tableRef,
    filterRef,
    selectRows,
    tableData,
    refreshTable,
  }));

  const selectableConfig = hasSelect
    ? {
        rowSelection: {
          type: 'checkbox',
          selectedRowKeys: selectRows.map((item) => item[rowKey]),
          disabledRowKeys: disabledKeys,
        },
        onSelectChange: (v) => {
          setSelectRows(v);
        },
      }
    : {};

  const toolbarSubActions = [
    ...(batchRender?.({ selectRows, tableData }) || []),
    ...ProTablePlusProps?.toolbar?.subActions || [],
  ].filter(Boolean);

  return (
    <div className={cx('list__container', 'COMMON-MPC-List', props.warpperClassName)}>
      <div className={cx('list__content', props.contentClassName)}>
        {props?.HeaderPlace}
        <ProTablePlus
          size="medium"
          sticky={
            {
              offsetHeader: localPx2rem(-16) as number,
              getContainer: () => document.querySelector('.COMMON-MPC-List'),
            } as any
          }
          overflow="hidden"
          rowKey={rowKey}
          {...selectableConfig}
          // isShowLoading={isShowLoading}
          ref={tableRef}
          // headerCollapse={false}
          filterRef={filterRef}
          columns={memoColumns}
          tableRefresh
          request={{ queryService }}
          pagination={{ pageSize: 20, pageSizeOptions: [10, 20, 50, 100] }}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandIconColumnIndex: memoColumns.findIndex(
              (item) => item.dataIndex === expandIconColumnKey
            ),
            needIndentSpaced: props.isTreeTable,
            onExpand: handleExpand,
            onExpandedRowsChange: (expandedKeys) => {
              setExpandedRowKeys(expandedKeys);
            },
            expandedRowKeys,
          }}
          isShowTreeLine
          onRow={(r) => ({
            style: { cursor: 'pointer' },
            onClick: (event) => {
              const clickEle = event.target as HTMLElement;
              const selection = window.getSelection && window.getSelection();
              if (
                (selection && selection.type === 'Range') ||
                (clickEle &&
                  (clickEle.tagName === 'A' ||
                    clickEle.classList.contains('rock-checkbox-input') ||
                    clickEle.classList.contains('rock-table-expand-icon') ||
                    clickEle.closest('.rock-table-expand-icon') ||
                    clickEle.closest('.rock-modal-wrap')))
              ) {
                return;
              }
              if (onClickRow) {
                onClickRow(r);
                return;
              }
              if (location && urlParams?.projectId !== r.id) {
                history.push({
                  ...location,
                  search: `?projectId=${r.id}`,
                });
              }
              handleClickRow(r);
            },
          })}
          rowClassName={(r: any, i: number) => {
            if (
              menuKey === 'myDemand' &&
              ['closed', 'abandon'].includes((r.state || '').toLowerCase())
            ) {
              return 'mpc-table-row-text-grey';
            }
            return '';
          }}
          // 表格全屏设置
          // tableFullScreen
          {...ProTablePlusProps}
          toolbar={toolbarConfig ? toolbarConfig : {
            actionsInTitle: true,
            search: {
              placeholder: '请输入单号/需求标题',
              id: 'search_text',
            },
            tabSize: 'medium',
            ...ProTablePlusProps?.toolbar,
            filterSetting: {
              defaultKeys: filterKeys?.map?.((key) => getKey(key, 'searchKey')),
              // 筛选项设置
              disableAddButton: true,
              showCondition: false,
              ...(ProTablePlusProps?.toolbar?.filterSetting as any),
            },
            subActions: toolbarSubActions.length ? toolbarSubActions : undefined,
          }}
        />
      </div>
      <PageFooter />
    </div>
  );
};

export default React.forwardRef(MPCList);
